import { type Order, mapOrderResponse } from "./order";
import { type StandbyOrder, mapStandbyOrderResponse } from "./standbyOrder";

export enum UangJalanReportStatus {
  OPEN = "open",
  IN_REVIEW = "in_review",
  REVISION_REQUIRED = "revision_required",
  VERIFIED = "verified",
  APPROVED = "approved",
  APPROVED_WITH_OVERRIDE = "approved_with_override",
}

export const isReportApproved = (status: UangJalanReportStatus): boolean => {
  return (
    status === UangJalanReportStatus.APPROVED ||
    status === UangJalanReportStatus.APPROVED_WITH_OVERRIDE
  );
};

export const canDownloadUangJalanReportExcel = (status: UangJalanReportStatus): boolean => {
  return (
    status === UangJalanReportStatus.IN_REVIEW ||
    status === UangJalanReportStatus.VERIFIED ||
    status === UangJalanReportStatus.APPROVED ||
    status === UangJalanReportStatus.APPROVED_WITH_OVERRIDE
  );
};

export interface UangJalanReportHistoryChange {
  field: string;
  before?: unknown;
  after?: unknown;
}

export interface UangJalanReportHistory {
  userId: string;
  userName?: string;
  profilePictureUrl?: string | null;
  timestamp: number;
  changes: UangJalanReportHistoryChange[];
}

export interface UangJalanReport {
  _id: string;
  readableId: string;
  companyId: string;
  teamOperationsId: string;
  orderIds: string[];
  standbyOrderIds: string[];
  status: UangJalanReportStatus;
  creationCalendarDate: string;
  totalPaid: number;
  totalApprovedCost: number;
  difference: number;
  teamOperations?: { _id: string; name: string };
  histories?: UangJalanReportHistory[];
  orders?: Order[];
  standbyOrders?: StandbyOrder[];
}

export interface UangJalanReportSearchFilters {
  teamOperationsId?: string;
  status?: string;
  statuses?: string[];
}

export interface CreateUangJalanReportPayload {
  teamOperationsId: string;
  orderIds?: string[];
  standbyOrderIds?: string[];
}

export interface UpdateUangJalanReportOrdersPayload {
  orderIds?: string[];
  standbyOrderIds?: string[];
}

export const formatUangJalanReportHistoryValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "—";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
};

export const sortUangJalanReportHistoriesDesc = (
  histories: UangJalanReportHistory[],
): UangJalanReportHistory[] => {
  return [...histories].sort((a, b) => b.timestamp - a.timestamp);
};

export const mapUangJalanReportResponse = (response: any): UangJalanReport => {
  return {
    _id: response._id,
    readableId: response.readableId,
    companyId: response.companyId,
    teamOperationsId: response.teamOperationsId,
    orderIds: response.orderIds || [],
    standbyOrderIds: response.standbyOrderIds || [],
    status: response.status,
    creationCalendarDate: response.creationCalendarDate,
    totalPaid: response.totalPaid ?? 0,
    totalApprovedCost: response.totalApprovedCost ?? 0,
    difference: response.difference ?? 0,
    teamOperations: response.teamOperations,
    histories: response.histories,
    orders: response.orders?.map(mapOrderResponse),
    standbyOrders: response.standbyOrders?.map(mapStandbyOrderResponse),
  };
};

export const getReportClaimMembers = (report: UangJalanReport): Array<Order | StandbyOrder> => {
  return [...(report.orders ?? []), ...(report.standbyOrders ?? [])];
};

export const mapUangJalanReportsResponse = (response: any): UangJalanReport[] => {
  const reports = response?.data?.uangJalanReports ?? response?.uangJalanReports ?? [];
  return reports.map(mapUangJalanReportResponse);
};
